import { SelectionModel } from '@angular/cdk/collections';
import { NgIf } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, TemplateRef } from '@angular/core';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTabsModule } from '@angular/material/tabs';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { ToastrService } from 'ngx-toastr';
import { combineLatest } from 'rxjs';
import { BreadcrumbComponent } from 'src/app/shared/components/breadcrumb/breadcrumb.component';
import { SelectBasicDepartmentPopupComponent } from 'src/app/shared/components/select-basic-department-popup/select-basic-department-popup.component';
import { LogType } from 'src/app/shared/enum/log-type.enum';
import { BreadcrumbItem } from 'src/app/shared/models/breadcrumb.interface';
import { DialogData } from 'src/app/shared/models/dialog-data.model';
import { DialogItemModel } from 'src/app/shared/models/dialog-item.model';
import { LogModel } from 'src/app/shared/models/log.model';
import { OptionModel } from 'src/app/shared/models/optionModel';
import { LogService } from 'src/app/shared/services/log.service';
import commonFunction from 'src/app/shared/utils/common';
import { environment } from 'src/environments/environment';
import { UserService } from '../../../services/user.service';
import { SecurityGroup } from '../../roles/models/security-group.model';
import { RoleDialogComponent } from '../dialog/role-dialog/role-dialog.component';
import { SearchEmployeeFilter } from '../models/search-emplyee-filter';

export interface RoleModel {
  id: number;
  name: string;
  description: string;
}

@Component({
  selector: 'app-user-add-edit',
  standalone: true,
  imports: [
    BreadcrumbComponent,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatIconModule,
    MatButtonModule,
    MatInputModule,
    MatSelectModule,
    MatTableModule,
    MatTabsModule,
    MatCheckboxModule,
    NgIf,
    AngularSvgIconModule,
    TranslateModule,
    RouterModule,
  ],
  templateUrl: './user-add-edit.component.html',
  styleUrl: './user-add-edit.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserAddEditComponent {
  displayedColumns: string[] = ['select', 'name', 'description'];
  dataSource = new MatTableDataSource<RoleModel>();
  selection = new SelectionModel<RoleModel>(true, []);
  rolesOfUser: any[] = [];

  breadcrumbItems: BreadcrumbItem[] = [];
  disableModeUpsertUser: boolean = environment.disableModeUpsertUser;
  cannotEditUserRoleWarning = '';

  generalForm!: UntypedFormGroup;
  confirmErrorMatcher = {
    isErrorState: (control: FormControl): boolean => {
      const controlInvalid = control.touched && control.invalid;
      const formInvalid = control.touched && this.generalForm.controls['password']?.touched && this.generalForm.invalid;
      return controlInvalid || formInvalid;
    },
  };

  passwordMatchValidator(g: FormGroup) {
    const password = this.generalForm.controls['password']?.value;
    const confirm = this.generalForm.controls['repeatPassword']?.value;
    return password && confirm && password === confirm ? null : { mismatch: true };
  }

  isCurrentUserAdmin = environment.server?.hasAdminRole;
  isEditUserAdmin = false;
  isExistedLoginName = false;
  isAddMode = false;
  disabledSaveButton = false;
  enableEditRole = true;
  allDepartments: any[] = [];
  roles: SecurityGroup[] = [];
  getEmployeeFilter: SearchEmployeeFilter = new SearchEmployeeFilter();
  changePasswordDialog!: MatDialogRef<any, any>;

  constructor(
    public formBuilder: UntypedFormBuilder,
    private translate: TranslateService,
    private dialog: MatDialog,
    private router: Router,
    private route: ActivatedRoute,
    private userService: UserService,
    private logService: LogService,
    private toastr: ToastrService,
    private cdr: ChangeDetectorRef,
  ) {
    this.generalForm = formBuilder.group(
      {
        firstName: ['', [Validators.required, Validators.pattern(/^(?!\s*$).+/)]],
        lastName: ['', [Validators.required, Validators.pattern(/^(?!\s*$).+/)]],
        email: ['', [Validators.required, Validators.email]],
        loginName: ['', [Validators.required]],
        loginDomain: [''],
        password: [''],
        repeatPassword: [''],
        departmentId: [''],
        departmentName: [''],
        title: [''],
        comment: [''],
        address: [''],
        zipCode: [''],
        place: [''],
        phoneHome: [''],
        phoneWork: [''],
        beeper: [''],
        fax: [''],
        phoneInternal: [''],
        phoneMobile: [''],
        callNumber: [''],
        employeeNo: [''],
        hasEditPassword: [false],
      },
      {
        validator: this.confirmedValidator('password', 'repeatPassword'),
      },
    );
  }

  ngOnInit() {
    this.initI18n();
    if (this.disableModeUpsertUser) {
      this.generalForm.disable();
    }

    this.route.params.subscribe((params) => {
      if (params['id']) {
        this.isAddMode = false;
        this.getEmployeeFilter.pageIndex = 1;
        this.getEmployeeFilter.pageSize = 0;
        this.getEmployeeFilter.employeeId = params['id'];
        this.loadDataForEditMode();
      } else {
        this.isAddMode = true;
        this.loadDataForAddMode();
      }
      this.buildBreadcrumb();
    });
  }

  loadDataForAddMode(): void {
    this.generalForm.controls['password'].setValidators([Validators.required]);
    this.generalForm.controls['repeatPassword'].setValidators([Validators.required]);
    this.generalForm.controls['departmentId'].setValidators([Validators.required]);

    this.generalForm.controls['password'].updateValueAndValidity();
    this.generalForm.controls['repeatPassword'].updateValueAndValidity();
    this.generalForm.controls['departmentId'].updateValueAndValidity();

    combineLatest([this.userService.getSecurityGroups(true), this.userService.getAllDepartments(false)]).subscribe(
      ([allRoles, allDepartments]) => {
        this.roles = allRoles;
        this.allDepartments = allDepartments;
      },
    );
  }

  loadDataForEditMode() {
    combineLatest([
      this.userService.getSecurityGroups(true),
      this.userService.getRolesHaveLeaderAndDeputyByEmployeeId(this.getEmployeeFilter.employeeId),
      this.userService.getEmployee(this.getEmployeeFilter),
      this.userService.getAllDepartments(false),
    ]).subscribe(([allRoles, roles, employeeRes, allDepartments]) => {
      this.roles = allRoles;
      this.rolesOfUser = roles;
      this.dataSource = new MatTableDataSource<RoleModel>(roles);
      this.cdr.detectChanges();
      this.userService.checkAdminRole(roles.map((item) => item.id)).subscribe((isAdmin) => {
        this.isEditUserAdmin = isAdmin;
        this.enableEditRole = this.isCurrentUserAdmin || !this.isEditUserAdmin;
      });
      this.generalForm.patchValue(employeeRes[0]);
      this.generalForm.patchValue({ repeatPassword: employeeRes[0].password });
      this.allDepartments = allDepartments;
    });
  }

  buildBreadcrumb() {
    this.breadcrumbItems = [
      {
        name: 'SIDE_BAR.HOME',
        url: '/',
      },
      {
        name: 'SIDE_BAR.MANAGE_USER',
        url: '/admin/users',
      },
      {
        name: this.isAddMode ? 'USER.ADD_USER' : 'USER.EDIT_USER',
        url: '',
      },
    ];
  }

  initI18n() {
    this.translate.get(['USER.DISABLE_EDIT_ROLE']).subscribe((res) => {
      this.cannotEditUserRoleWarning = res['USER.DISABLE_EDIT_ROLE'];
    });
  }

  onSubmit() {
    if (this.generalForm.invalid) {
      return;
    }

    const permissions = this.roles.filter((item) =>
      this.rolesOfUser.some((selectedRole) => selectedRole.id == item.id),
    );

    this.disabledSaveButton = true;
    const params = {
      departmentIds: [],
      positions: [],
      employee: this.generalForm.value,
      permissions: permissions,
    };
    if (this.getEmployeeFilter.employeeId) {
      console.log(params);
      this.userService.updateEmployee(params).subscribe(() => {
        this.toastr.success(this.translate.instant('USER.USER_UPDATED'));
        this.saveLogUpdateEmployee(
          this.generalForm.value.firstName + ' ' + this.generalForm.value.lastName,
          this.generalForm.value.id,
        );
        this.router.navigate(['admin/users']);
      });
    } else {
      this.userService.createEmployee(params).subscribe(() => {
        this.toastr.success(this.translate.instant('USER.NEW_USER_CREATED'));
        this.saveLogCreateEmployee(this.generalForm.value.firstName + ' ' + this.generalForm.value.lastName);
        this.router.navigate(['admin/users']);
      });
    }
  }

  saveLogCreateEmployee(name: string): void {
    const log: LogModel = new LogModel();
    log.logType = LogType.newUsers;
    log.description = ` created user <strong>${name}</strong>`;
    log.descriptionNo = ` opprettet bruker <strong>${name}</strong>`;
    this.logService.saveLog(log).subscribe();
  }

  saveLogUpdateEmployee(name: string, id: string): void {
    const log: LogModel = new LogModel();
    log.logType = LogType.newUsers;
    log.description = ` updated user <strong>${name}</strong> ${id ? ', ID ' + id : ''} `;
    log.descriptionNo = ` oppdatert bruker <strong>${name}</strong> ${id ? ', ID ' + id : ''} `;
    this.logService.saveLog(log).subscribe();
  }

  /** Whether the number of selected elements matches the total number of rows. */
  isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this.dataSource.data.length;
    return numSelected === numRows;
  }

  onEditPassword(template: TemplateRef<any>) {
    this.changePasswordDialog = this.dialog.open(template, {
      autoFocus: false,
      height: 'fit-content',
      width: '532px',
      panelClass: ['none-padding-popup', 'overflow-hidden'],
      disableClose: true,
    });
  }

  onClickSelectDepartment(): void {
    if (this.disableModeUpsertUser) {
      return;
    }
    const data: DialogData = new DialogData();
    data.selectNode = new OptionModel('', 0);
    data.items = this.allDepartments.map(
      (item) => new DialogItemModel(item.name, item.id, item.parentId, item.isReadOnly),
    );
    data.showRoot = true;
    data.canChooseRoot = true;
    data.isGuidId = false;
    data.title = this.translate.instant('USER.SELECT_DEPARTMENT');
    const dialogRef = this.dialog.open(SelectBasicDepartmentPopupComponent, {
      minWidth: `650px`,
      height: `395px`,
      data,
      disableClose: true,
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result && result.id !== this.generalForm.value.id) {
        this.generalForm.patchValue({ departmentId: result.id });
        this.generalForm.patchValue({
          departmentName: result.id === 0 ? '' : commonFunction.buildLocationText(result.id, this.allDepartments),
        });
      }
    });
  }

  bulkDeleteRole(): void {
    this.rolesOfUser = this.rolesOfUser.filter((item) => !this.selection.selected.some((id) => id == item.id));
    this.dataSource = new MatTableDataSource<RoleModel>(this.rolesOfUser);
    this.cdr.detectChanges();
    this.selection.clear();
  }

  onCancelChangePasswordClick() {
    this.generalForm.patchValue({ password: '', repeatPassword: '' });
    this.changePasswordDialog.close();
  }

  onConfirmChangePasswordClick() {
    this.changePasswordDialog.close();
  }

  /** Selects all rows if they are not all selected; otherwise clear selection. */
  toggleAllRows() {
    if (this.isAllSelected()) {
      this.selection.clear();
      return;
    }

    this.selection.select(...this.dataSource.data);
  }

  /** The label for the checkbox on the passed row */
  checkboxLabel(row?: RoleModel): string {
    if (!row) {
      return `${this.isAllSelected() ? 'deselect' : 'select'} all`;
    }
    return `${this.selection.isSelected(row) ? 'deselect' : 'select'} row ${row.id + 1}`;
  }

  onCheckExist(): void {
    if (this.generalForm.controls['loginName'].valid)
      if (this.isAddMode) {
        this.userService.isExistedCreateLoginName(this.generalForm.controls['loginName'].value).subscribe((res) => {
          this.isExistedLoginName = res;
        });
      } else {
        this.userService
          .isExistedEditLoginName(this.generalForm.controls['loginName'].value, +this.getEmployeeFilter.employeeId)
          .subscribe((res) => {
            this.isExistedLoginName = res;
          });
      }
  }

  openRoleDialog() {
    const dialogRef = this.dialog.open(RoleDialogComponent, {
      data: { title: 'New role' },
      width: '620px',
    });

    dialogRef.componentInstance.roleItems = this.roles.map((item) => new OptionModel(item.name, item.id.toString()));

    dialogRef.afterClosed().subscribe((res) => {
      console.log(res);
      if (res) {
        const role = this.roles.find((item) => item.id == res) || new SecurityGroup();
        if (!this.rolesOfUser.some((item) => item.id == role.id)) {
          this.rolesOfUser.push(role);
        }

        this.dataSource = new MatTableDataSource<RoleModel>(this.rolesOfUser);
        this.cdr.detectChanges();
        console.log(this.dataSource.data);
      }
    });
  }

  confirmedValidator(controlName: string, matchingControlName: string) {
    return (formGroup: FormGroup) => {
      const control = formGroup.controls[controlName];
      const matchingControl = formGroup.controls[matchingControlName];
      if (matchingControl.errors && !matchingControl.errors['confirmedValidator']) {
        return;
      }
      if (control.value !== matchingControl.value) {
        matchingControl.setErrors({ confirmedValidator: true });
      } else {
        matchingControl.setErrors(null);
      }
    };
  }
}
